import { defineStore } from 'pinia';
import { collection, onSnapshot } from 'firebase/firestore';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { useFirebaseApp, useFirestore } from 'vuefire';
import { jwtDecode } from 'jwt-decode';

// Bookmarks are written through the cloud API (via the local /api/bookmarks
// proxy) and mirrored back in real time to users/{userId}/bookmarks in
// Firestore — the snapshot listener below is the single source of truth for
// heart state and the /bookmarks page.
export const useBookmarkStore = defineStore('bookmark', {
  state: () => ({
    bookmarks: {}, // live mirror of users/{userId}/bookmarks, keyed by bookmark id
    loaded: false,
    initialized: false,
    watchedUserId: null,
    unsubscribe: null,
  }),
  getters: {
    bookmarkList: (state) => Object.values(state.bookmarks),
  },
  actions: {
    // Client-only and idempotent. Follows auth state so the mirror starts as
    // soon as the internal user id (custom `user` JWT claim) is available and
    // stops on sign-out.
    init() {
      if (this.initialized || process.server) return;
      this.initialized = true;
      const auth = getAuth(useFirebaseApp());
      onIdTokenChanged(auth, async (user) => {
        if (!user) {
          this.stopWatching();
          return;
        }
        const token = await user.getIdToken();
        const userId = jwtDecode(token).user?.id;
        if (userId) {
          this.startWatching(userId);
        }
      });
    },
    startWatching(userId) {
      if (this.watchedUserId === String(userId)) return;
      this.stopWatching();
      this.watchedUserId = String(userId);
      const db = useFirestore();
      this.unsubscribe = onSnapshot(
        collection(db, 'users/' + userId + '/bookmarks'),
        (snapshot) => {
          const bookmarks = {};
          snapshot.forEach((doc) => {
            bookmarks[doc.id] = { id: doc.id, ...doc.data() };
          });
          this.bookmarks = bookmarks;
          this.loaded = true;
        },
        (error) => {
          console.error('[bookmarkStore] bookmarks watch failed', error);
        }
      );
    },
    stopWatching() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      this.watchedUserId = null;
      this.bookmarks = {};
      this.loaded = false;
    },
    find({ announceId, subjectType, subjectId }) {
      return (
        this.bookmarkList.find((bookmark) => {
          if (announceId && String(bookmark.announce_id) === String(announceId)) {
            return true;
          }
          return (
            subjectType &&
            subjectId &&
            bookmark.subject_type === subjectType &&
            String(bookmark.subject_id) === String(subjectId)
          );
        }) ?? null
      );
    },
    async getToken() {
      const auth = getAuth(useFirebaseApp());
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      return auth.currentUser.getIdToken();
    },
    // Safe to call twice — upstream dedupes (200 instead of 201). The mirror
    // adds the document too, but merge the response locally so the heart
    // flips without waiting for the snapshot.
    async add(payload) {
      const { $cloud } = useNuxtApp();
      await $cloud.ensureUserRegistered();
      const token = await this.getToken();
      const response = await $fetch('/api/bookmarks', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: payload,
      });
      const bookmark = response?.data ?? response;
      if (bookmark?.id) {
        this.bookmarks = {
          ...this.bookmarks,
          [bookmark.id]: { ...payload, ...bookmark, id: String(bookmark.id) },
        };
      }
      return bookmark;
    },
    // Remove locally as well so the UI updates before the mirror catches up.
    async remove(id) {
      const token = await this.getToken();
      await $fetch('/api/bookmarks/' + id, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const bookmarks = { ...this.bookmarks };
      delete bookmarks[id];
      this.bookmarks = bookmarks;
    },
  },
});
